import * as React from "react"
import axios from "axios"
import { useAuth0 } from "@auth0/auth0-react"
import { useCustomerData } from "context/customer-provider"
import config from "config"
import { isSignInRequired, signInAgain } from "auth/sessionEnded"

/**
 * Set fetchy presets. To avoid setting URL and token for every single fetch.
 * Because most of our fetch operations use the same api base endpoint.
 */
export const useFetchSetup = () => {
  // Track which customerId the global axios interceptor is currently
  // configured for. Set inside the effect, so it lags the live customerId
  // until React has actually committed the interceptor swap. AppWrapper
  // uses the derived `fetchReady` to unmount the rest of the tree during
  // the swap — otherwise react-query effects deeper in the tree fire
  // bottom-up before this one runs, and trigger fetches against the
  // previous customer's URL prefix.
  const [configuredCustomerId, setConfiguredCustomerId] = React.useState<string>("")

  const { customerId } = useCustomerData()
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()
  // the latest SDK functions, without re-installing the interceptor
  const getTokenRef = React.useRef(getAccessTokenSilently)
  getTokenRef.current = getAccessTokenSilently
  const loginRef = React.useRef(loginWithRedirect)
  loginRef.current = loginWithRedirect
  const lastTokenRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!customerId) return

    // The token is asked for per request rather than being a precondition:
    // the app (rendered from a boot snapshot) may start its page requests
    // while Auth0 is still establishing the session — they wait here for the
    // SDK, which answers from its cache once it has a token and renews it
    // when it expires.
    const interceptor = axios.interceptors.request.use(async (axiosConfig) => {
      let token: string
      try {
        token = await getTokenRef.current({ authorizationParams: { audience: config.auth0Audience } })
        lastTokenRef.current = token
      } catch (error) {
        if (isSignInRequired(error)) {
          // Only signing in again helps (and the SDK may already have signed
          // out locally). Start it here — the token hook may be holding a
          // token and never ask — and park the request: failing it would only
          // flash errors in the moment before the redirect.
          signInAgain(loginRef.current)
          return new Promise<never>(() => undefined)
        }
        // Anything else (a renewal timing out, a network blip) is shared by
        // every request waiting on the SDK: carry on with the last token
        // rather than failing them all; the API answers 401 if it has expired.
        if (!lastTokenRef.current) throw error
        token = lastTokenRef.current
      }
      axiosConfig.baseURL = `${config.railApiBasePath}/${customerId}/`
      axiosConfig.headers.Authorization = `Bearer ${token}`
      return axiosConfig
    })
    setConfiguredCustomerId(customerId)

    return () => axios.interceptors.request.eject(interceptor)
  }, [customerId])

  return {
    fetchReady: Boolean(customerId) && configuredCustomerId === customerId,
  }
}
